import React from 'react';
import { Accordion, Badge, makeStyles, tokens, useId } from '@fluentui/react-components';
import { MOBILE_NAV_MAX_WIDTH_BREAKPOINT } from '../../app/common/constants/SiteConstants';
import IUserRoles from '../../utils/types/IUserRoles';
import AssignedRoleCard from './AssignedRoleCard';
import type { AssignedRoleGroup } from './AssignedRoleGroup';

const useStyles = makeStyles({
  heading: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  title: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
    },
  },
  description: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
  },
  roles: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
  },
});

interface AssignedRoleSectionProps {
  roleAssignmentsArray: IUserRoles[];
}

const AssignedRolesSection = ({ roleAssignmentsArray }: AssignedRoleSectionProps): JSX.Element => {
  const styles = useStyles();
  const titleId = useId('assigned-roles-title');
  const assignmentsByRole: Record<number, AssignedRoleGroup> = {};
  roleAssignmentsArray.forEach((assignment) => {
    const { roleId, roleName } = assignment;
    if (!assignmentsByRole[roleId]) {
      assignmentsByRole[roleId] = {
        roleId,
        roleName,
        assignments: [],
      };
    }

    assignmentsByRole[roleId].assignments.push(assignment);
  });
  const roles = Object.values(assignmentsByRole);

  return (
    <section aria-labelledby={titleId}>
      <div className={styles.heading}>
        <h2 id={titleId} className={styles.title}>
          Assigned Roles
        </h2>
        <Badge
          appearance="tint"
          color="informative"
          aria-label={`${roles.length} assigned role${roles.length === 1 ? '' : 's'}`}
        >
          {roles.length}
        </Badge>
      </div>
      <p className={styles.description}>
        Expand a role to see its recorded company assignments and scope.
      </p>
      {roles.length > 0 ? (
        <Accordion multiple collapsible defaultOpenItems={[]} className={styles.roles}>
          {roles.map((roleGroup) => (
            <AssignedRoleCard key={roleGroup.roleId} role={roleGroup} />
          ))}
        </Accordion>
      ) : (
        <p className={styles.emptyState}>No roles assigned</p>
      )}
    </section>
  );
};

export default AssignedRolesSection;
